import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminGroup } from '@/lib/api'
import AssetImageField from '@/components/AssetImageField'

const SOCIAL_PLATFORMS = [
  'Instagram',
  'TikTok',
  'YouTube',
  'Twitter',
  'Facebook',
  'Weverse',
  'Spotify',
]

interface FormData {
  id: string
  slug: string
  name: string
  tagline: string
  description: string
  debut_date: string
  color_primary: string
  cover_image_url: string
  logo_url: string
  social_handles: Record<string, string>
  is_active: boolean
}

const blank: FormData = {
  id: '', slug: '', name: '', tagline: '', description: '', debut_date: '',
  color_primary: '#ffffff', cover_image_url: '', logo_url: '', social_handles: {}, is_active: true,
}

function toFormData(g: AdminGroup): FormData {
  return {
    id: g.id, slug: g.slug, name: g.name, tagline: g.tagline, description: g.description,
    debut_date: g.debut_date, color_primary: g.color_primary, cover_image_url: g.cover_image_url,
    logo_url: g.logo_url ?? '',
    social_handles: (g.social_handles as Record<string, string>) ?? {},
    is_active: g.is_active,
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800">
        <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  )
}

export default function GroupFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = useAuth()

  const isEdit = Boolean(id)

  const [form, setForm] = useState<FormData>(blank)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isEdit || !token) return

    const stateGroup = (location.state as { group?: AdminGroup } | null)?.group
    if (stateGroup) {
      setForm(toFormData(stateGroup))
      setLoading(false)
      return
    }

    api.getAdminGroups(token).then((items) => {
      const found = items.find((g) => g.id === id)
      if (found) setForm(toFormData(found))
    }).finally(() => setLoading(false))
  }, [id, isEdit, token, location.state])

  const set = (field: keyof FormData, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }))

  const setSocial = (platform: string, value: string) =>
    setForm((prev) => ({
      ...prev,
      social_handles: { ...prev.social_handles, [platform]: value },
    }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setSaving(true)
    try {
      const socialHandles: Record<string, string> = {}
      for (const [k, v] of Object.entries(form.social_handles)) {
        if (v.trim()) socialHandles[k] = v.trim()
      }

      const payload = {
        id: form.id, slug: form.slug, name: form.name, tagline: form.tagline,
        description: form.description, debut_date: form.debut_date, color_primary: form.color_primary,
        cover_image_url: form.cover_image_url, logo_url: form.logo_url || null,
        social_handles: socialHandles, is_active: form.is_active,
      }

      if (isEdit && id) {
        const { id: _id, slug: _slug, ...updatePayload } = payload
        void _id; void _slug
        const updated = await api.updateGroup(token, id, updatePayload)
        navigate(`/groups/${id}`, { replace: true, state: { group: updated } })
      } else {
        await api.createGroup(token, payload)
        navigate(-1)
      }
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const input = 'w-full bg-zinc-800 text-white rounded-lg px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600'
  const label = 'block text-xs text-zinc-400 mb-1.5'

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit}>
        {/* Sticky top bar */}
        <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors mb-3"
            >
              <ChevronLeft size={14} />
              Kembali
            </button>
            <h1 className="text-xl font-bold text-white">{isEdit ? `Edit Group` : 'New Group'}</h1>
            {isEdit && <p className="text-zinc-500 text-sm mt-1">{form.name}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-5 py-2 text-sm text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 text-sm font-semibold bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Menyimpan…' : isEdit ? 'Simpan Perubahan' : 'Buat Group'}
            </button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">

        {/* ── Informasi Dasar ─────────────────────────────── */}
        <Section title="Informasi Dasar">
          <div>
            <label className={label}>Nama Group <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              required
              placeholder="Nama grup artis"
              className={input}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={label}>Tagline</label>
              <input
                type="text"
                value={form.tagline}
                onChange={(e) => set('tagline', e.target.value)}
                placeholder="Slogan atau deskripsi singkat"
                className={input}
              />
            </div>
            <div>
              <label className={label}>Tanggal Debut</label>
              <input
                type="date"
                value={form.debut_date}
                onChange={(e) => set('debut_date', e.target.value)}
                className={input + ' [color-scheme:dark]'}
              />
            </div>
          </div>

          <div>
            <label className={label}>Deskripsi</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={4}
              placeholder="Deskripsi lengkap tentang grup ini"
              className={input + ' resize-y'}
            />
          </div>
        </Section>

        {/* ── Identitas ───────────────────────────────────── */}
        <Section title="Identitas">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={label}>
                ID{' '}
                <span className="text-zinc-600 font-normal normal-case tracking-normal">
                  — primary key
                </span>
              </label>
              <input
                type="text"
                value={form.id}
                onChange={(e) => set('id', e.target.value)}
                required={!isEdit}
                disabled={isEdit}
                placeholder="grp-nama-group"
                className={input + ' disabled:opacity-40 disabled:cursor-not-allowed'}
              />
            </div>
            <div>
              <label className={label}>
                Slug{' '}
                <span className="text-zinc-600 font-normal normal-case tracking-normal">
                  — URL path
                </span>
              </label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => set('slug', e.target.value)}
                required={!isEdit}
                disabled={isEdit}
                placeholder="nama-group"
                className={input + ' disabled:opacity-40 disabled:cursor-not-allowed'}
              />
            </div>
          </div>
          {isEdit && (
            <p className="text-xs text-zinc-600">
              ID dan slug tidak dapat diubah setelah group dibuat.
            </p>
          )}
        </Section>

        {/* ── Visual ──────────────────────────────────────── */}
        <Section title="Visual">
          <div className="grid grid-cols-3 gap-5">
            <div className="col-span-2">
              <label className={label}>Cover Image</label>
              <AssetImageField
                value={form.cover_image_url}
                onChange={(url) => set('cover_image_url', url)}
                label="Cover Image"
                feature="groups"
              />
            </div>
            <div>
              <label className={label}>Logo</label>
              <AssetImageField
                value={form.logo_url}
                onChange={(url) => set('logo_url', url)}
                label="Logo"
                feature="groups"
              />
            </div>
          </div>

          <div>
            <label className={label}>Accent Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form.color_primary}
                onChange={(e) => set('color_primary', e.target.value)}
                className="h-9 w-12 rounded-lg border border-zinc-700 bg-zinc-800 cursor-pointer p-0.5"
              />
              <input
                type="text"
                value={form.color_primary}
                onChange={(e) => set('color_primary', e.target.value)}
                className={input + ' w-36'}
                placeholder="#ffffff"
              />
              <div
                className="flex-1 h-9 rounded-lg border border-zinc-700"
                style={{ background: form.color_primary }}
              />
            </div>
          </div>
        </Section>

        {/* ── Social Media ────────────────────────────────── */}
        <Section title="Social Media">
          <div className="grid grid-cols-2 gap-x-5 gap-y-3">
            {SOCIAL_PLATFORMS.map((platform) => (
              <div key={platform}>
                <label className={label}>{platform}</label>
                <input
                  type="url"
                  value={form.social_handles[platform] ?? ''}
                  onChange={(e) => setSocial(platform, e.target.value)}
                  placeholder="https://..."
                  className={input}
                />
              </div>
            ))}
          </div>
        </Section>

        {/* ── Pengaturan ──────────────────────────────────── */}
        <Section title="Pengaturan">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white font-medium">Status Group</p>
              <p className="text-xs text-zinc-500 mt-0.5">Group aktif akan tampil di halaman publik</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => set('is_active', !form.is_active)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                  form.is_active ? 'bg-green-500' : 'bg-zinc-600'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                    form.is_active ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={`text-xs font-medium w-14 ${form.is_active ? 'text-green-400' : 'text-zinc-500'}`}>
                {form.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </Section>

        </div>
      </form>
    </div>
  )
}
